import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface JobMatch {
  id: string;
  name: string;
  skills: string[];
  score: number;
}

export const useJobTopMatches = (jobId: string | undefined, limit: number = 10) => {
  const [matches, setMatches] = useState<JobMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTopMatches = async () => {
      if (!jobId) {
        setMatches([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch matches for this job, ordered by score descending, limited to top 'limit'
        const { data, error } = await supabase
          .from('matches')
          .select(`
            id,
            score,
            candidate_id,
            candidates (
              id,
              name,
              skills
            )
          `)
          .eq('job_id', jobId)
          .order('score', { ascending: false })
          .limit(limit);
        
        if (error) throw error;
        
        if (data) {
          // Map the DB results to our expected format
          const formattedMatches: JobMatch[] = data.map(match => ({
            id: match.candidate_id,
            name: match.candidates?.name || 'Unknown Candidate',
            skills: match.candidates?.skills || [],
            score: match.score || 0
          }));
          
          setMatches(formattedMatches);
        }
      } catch (err) {
        console.error('Error fetching top matches:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch matches');
      } finally {
        setLoading(false);
      }
    };

    fetchTopMatches();
  }, [jobId, limit]);

  return { matches, loading, error };
};